import LoaderImg from "@assets/images/loader.gif";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import LocalStorageService from "@core/services/local-storage.service";
import { getPaymentsUrl } from "@utils/paymentsUrl";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { toast } from "react-toastify";
import { companySearchTabs, dateRangeOptions, initialMetrics, listedCompanyFilter, metricRangeLabels } from "../enum";
import UpgradePlanModal from "./upgradePlan";
import ReactTooltip from "react-tooltip";
import { Modal } from "react-bootstrap";

const CompanySearchFilters = ({ filters, setFilters, activeTab, tradeDirection, tableData, countryFilterOptions, countryFilterLoading = false, hsCodeFilterOptions = [], hsCodeFilterLoading = false, onJurisdictionFilterOpen, onHsCodeFilterOpen, initialFilters, currentUserPlan, hasActiveFilters, hideHsCodeFilter = false, trackingSource = "company-search" }) => {
  const filterEventKey = trackingSource === "product-search" ? trackingEventDictionary.newMay2026ProductSearchFilterApply : trackingEventDictionary.companySearchFilterApply;
  const filterBarRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [hsCodeSearchTerm, setHsCodeSearchTerm] = useState("");
  const [pendingJurisdiction, setPendingJurisdiction] = useState(filters.jurisdiction);
  const [pendingHsCodes, setPendingHsCodes] = useState(filters.hsCode || []);
  const [showHsCodeModal, setShowHsCodeModal] = useState(false);
  const [pendingMetrics, setPendingMetrics] = useState(initialMetrics);
  const [upsellVisible, setUpsellVisible] = useState(false);
  const localStorageService = LocalStorageService.getService();
  const fromDate = new Date(filters.dateRange.startDate);
  const toDate = new Date(filters.dateRange.endDate);
  const diffMs = toDate - fromDate;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  const isTwoYearsOrLess = diffDays <= 731;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterBarRef.current && !filterBarRef.current.contains(e.target)) {
        setActiveFilter(null);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleFilter = (type) => {
    setActiveFilter((prev) => {
      const newFilter = prev === type ? null : type;
      if (newFilter === "jurisdiction") {
        onJurisdictionFilterOpen?.();
        setPendingJurisdiction(filters.jurisdiction);
        setSearchTerm("");
      }
      if (newFilter === "hsCode") {
        onHsCodeFilterOpen?.();
        setPendingHsCodes(filters.hsCode || []);
        setHsCodeSearchTerm("");
        setShowHsCodeModal(true);
      }
      if (newFilter === "metrics") {
        const displayMetrics = {};
        for (const key in filters.metrics) {
          const { from, to } = filters.metrics[key];
          if (from !== "" && from !== "-1" && to === "-1") {
            displayMetrics[key] = { from: "", to: from };
          } else if (to !== "" && to !== "-1" && from === "-1") {
            displayMetrics[key] = { from: to, to: "" };
          } else {
            displayMetrics[key] = {
              from: from === "-1" ? "" : from,
              to: to === "-1" ? "" : to,
            };
          }
        }
        setPendingMetrics(displayMetrics);
      }
      if (newFilter === null) {
        setSearchTerm("");
      }
      return newFilter;
    });
  };

  const handleDateSelect = (selection) => {
    setFilters((prev) => ({ ...prev, dateRange: selection }));
    setActiveFilter(null);
    trademoTrackingEvents(filterEventKey, { ...generateTrackingObject(trackingSource, window.location.pathname, currentUserPlan), filterType: "dateRange" });
  };

  const handleMetricsUpdate = (metricKey, rangeKey, value) => {
    if (/^\d*$/.test(value)) {
      setPendingMetrics((prev) => ({
        ...prev,
        [metricKey]: { ...prev[metricKey], [rangeKey]: value },
      }));
    }
  };

  const handleJurisdictionToggle = (id) => {
    setPendingJurisdiction((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  const handleJurisdictionSelectAll = () => {
    if (pendingJurisdiction.length === countryFilterOptions.length) {
      setPendingJurisdiction([]);
    } else {
      setPendingJurisdiction(countryFilterOptions.map((c) => c.key));
    }
  };

  const applyJurisdiction = () => {
    setFilters((prev) => ({ ...prev, jurisdiction: pendingJurisdiction }));
    setActiveFilter(null);
    setSearchTerm("");
    trademoTrackingEvents(filterEventKey, { ...generateTrackingObject(trackingSource, window.location.pathname, currentUserPlan), filterType: "jurisdiction" });
  };

  const handleHsCodeToggle = (value) => {
    setPendingHsCodes((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const handleHsCodeSelectAll = () => {
    const searchedHsKeys = hsCodeFilterOptions
      .filter((h) =>
        String(h.key || "")
          .toLowerCase()
          .includes(hsCodeSearchTerm.toLowerCase())
      )
      .map((h) => h.key);
    const isAllSearchedSelected = searchedHsKeys.length > 0 && searchedHsKeys.every((key) => pendingHsCodes.includes(key));

    if (isAllSearchedSelected) {
      setPendingHsCodes((prev) => prev.filter((key) => !searchedHsKeys.includes(key)));
    } else {
      setPendingHsCodes((prev) => [...new Set([...prev, ...searchedHsKeys])]);
    }
  };

  const applyHsCode = () => {
    setFilters((prev) => ({ ...prev, hsCode: pendingHsCodes }));
    setActiveFilter(null);
    setHsCodeSearchTerm("");
    setShowHsCodeModal(false);
    trademoTrackingEvents(filterEventKey, { ...generateTrackingObject(trackingSource, window.location.pathname, currentUserPlan), filterType: "hsCode" });
  };

  const handleListedCompanySelect = (selection) => {
    setFilters((prev) => ({ ...prev, listedCompany: selection }));
    setActiveFilter(null);
    trademoTrackingEvents(filterEventKey, { ...generateTrackingObject(trackingSource, window.location.pathname, currentUserPlan), filterType: "listedCompany" });
  };

  const validateMetrics = () => {
    for (const key in pendingMetrics) {
      const { from, to } = pendingMetrics[key];
      if (from === "" && to === "") continue;
      const f = from === "" ? null : Number(from);
      const t = to === "" ? null : Number(to);

      if (f !== null && f <= 0 && t === null) {
        toast.error("'From' value must be greater than 0");
        return false;
      }
      if (t !== null && t <= 0 && f === null) {
        toast.error("'To' value must be greater than 0");
        return false;
      }

      if (f !== null && t !== null && t < f) {
        toast.error("'To' cannot be less than 'From'");
        return false;
      }
    }

    return true;
  };

  const applyMetrics = () => {
    if (!validateMetrics()) return;
    const transformedMetrics = {};
    for (const key in pendingMetrics) {
      const { from, to } = pendingMetrics[key];
      const f = from === "" ? null : Number(from);
      const t = to === "" ? null : Number(to);

      if (f === null && t === null) {
        transformedMetrics[key] = { from: "", to: "" };
      } else if (f !== null && f > 0 && t === null) {
        transformedMetrics[key] = { from: "-1", to: from };
      } else if (f === null && t !== null && t > 0) {
        transformedMetrics[key] = { from: to, to: "-1" };
      } else {
        transformedMetrics[key] = { from: from, to: to };
      }
    }
    setFilters((prev) => ({ ...prev, metrics: transformedMetrics }));
    setActiveFilter(null);
    trademoTrackingEvents(filterEventKey, { ...generateTrackingObject(trackingSource, window.location.pathname, currentUserPlan), filterType: "metrics" });
  };

  const clearAll = () => {
    setFilters(initialFilters);
    setPendingJurisdiction([]);
    setPendingHsCodes([]);
    setPendingMetrics(initialMetrics);
    setActiveFilter(null);
    setSearchTerm("");
    setHsCodeSearchTerm("");
    const clearEventKey = trackingSource === "product-search" ? trackingEventDictionary.newMay2026ProductSearchFilterClear : trackingEventDictionary.companySearchReset;
    trademoTrackingEvents(clearEventKey, generateTrackingObject(trackingSource, window.location.pathname, currentUserPlan));
  };

  const filteredCountries = countryFilterOptions.filter((c) => c.key.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredHsCodes = hsCodeFilterOptions.filter((h) =>
    String(h.key || "")
      .toLowerCase()
      .includes(hsCodeSearchTerm.toLowerCase())
  );

  const getActiveMetrics = () => {
    return metricRangeLabels.filter((m) => {
      const range = filters.metrics[m.key];
      return range.from !== "" || range.to !== "";
    });
  };

  const upgradeNow = () => {
    window.location.href = getPaymentsUrl() + "payment?T=" + localStorageService.getAccessToken() + "&I=" + localStorageService.getSessionID() + "&D=" + localStorageService.getSessionExpiryDate() + "&type=plans&P=" + localStorageService.getUserProduct();
  };

  const isMetricsDisabled = useMemo(() => {
    const metricesArr = Object.values(pendingMetrics);
    if (metricesArr.every((m) => m.from === "" && m.to === "")) return true;

    const hasValidMetrics = metricesArr.some((m) => {
      const f = m.from === "" ? null : Number(m.from);
      const t = m.to === "" ? null : Number(m.to);
      return (f !== null && f > 0) || (t !== null && t > 0);
    });

    return !hasValidMetrics;
  }, [pendingMetrics]);

  const jurisdictionFilterLabel = activeTab === companySearchTabs.PRODUCT ? (tradeDirection === "import" ? "Country of Import" : "Country of Export") : "Jurisdiction Country";

  if (tableData.length === 0 && !hasActiveFilters) return <></>;

  return (
    <div ref={filterBarRef} className="tw-flex tw-flex-col tw-gap-3 tw-text-sm">
      <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
        {upsellVisible && <UpgradePlanModal onClose={() => setUpsellVisible(false)} header="Upgrade Now" subHeading="The date range you are trying to access requires an upgrade to your current plan subscription." callBack={upgradeNow} buttonText="Upgrade Now" />}
        {/* Date Range Filter */}
        {activeTab === companySearchTabs.PRODUCT && (
          <div className="tw-relative tw-inline-block">
            <div onClick={() => toggleFilter("dateRange")} className={`tw-flex tw-items-center tw-gap-1 tw-border tw-border-[#e7e7e7] tw-cursor-pointer tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-semibold tw-shadow-button ${activeFilter === "dateRange" ? "tw-bg-gray-100" : "tw-bg-white"} tw-h-8`}>
              <span className="tw-flex tw-items-center tw-gap-1">
                <span className="material-symbols-outlined">calendar_month</span>
                {filters.dateRange.label}
              </span>
            </div>

            {activeFilter === "dateRange" && (
              <div className="tw-absolute tw-z-10 tw-right-0 tw-mt-1 tw-w-56 tw-rounded-xl tw-overflow-hidden tw-bg-white tw-border tw-border-gray-200 tw-shadow-2xl">
                <div className="tw-divide-y">
                  {dateRangeOptions.map((option) => {
                    const disabled = currentUserPlan && !option.accessToPlans.includes(currentUserPlan);
                    return (
                      <div key={option.label} onClick={() => (disabled ? setUpsellVisible(option) : handleDateSelect(option))} className={`tw-flex tw-items-center tw-gap-1 tw-cursor-pointer tw-px-4 tw-py-2 tw-text-sm tw-transition-all ${disabled ? "tw-text-gray-500 tw-flex tw-items-center tw-gap-1" : option.label === filters.dateRange.label ? "tw-bg-primary-purple-50 tw-font-medium" : "tw-text-gray-700 hover:tw-bg-gray-50"}`}>
                        <input type="radio" className="!tw-h-4 !tw-m-0 !tw-w-4 tw-accent-primary-purple-600 disabled:tw-cursor-not-allowed" defaultChecked={option.label === filters.dateRange.label} disabled={disabled} />
                        {option.label} {disabled && <img alt="thunder" width={20} src="https://res.cloudinary.com/drzta9shk/image/upload/v1684478309/intel/icons/Lightning-alt_t9mkjb.svg" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Jurisdiction Country Filter */}
        <div className="tw-relative tw-inline-block">
          <div onClick={() => toggleFilter("jurisdiction")} className={`tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-border tw-border-[#e7e7e7] tw-cursor-pointer tw-px-3 tw-py-1.5 tw-rounded-lg tw-shadow-button ${activeFilter === "jurisdiction" ? "tw-bg-gray-100" : "tw-bg-white"} tw-h-8`}>
            <span className="material-symbols-outlined">filter_alt</span>
            <span>{jurisdictionFilterLabel}</span>
            {filters.jurisdiction.length > 0 && (
              <>
                <span data-for="jurisdiction-filter-tooltip" data-tip={filters.jurisdiction.join(" | ")} className="tw-bg-primary-purple-100 tw-text-primary-purple-600 tw-pl-1 tw-pr-0.5 tw-text-xs tw-font-medium tw-rounded-full tw-flex tw-items-center">
                  {filters.jurisdiction.length}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilters((prev) => ({ ...prev, jurisdiction: [] }));
                      setPendingJurisdiction([]);
                    }}
                    className="tw-text-inherit tw-bg-transparent">
                    <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor">
                      <path d="m291-240-51-51 189-189-189-189 51-51 189 189 189-189 51 51-189 189 189 189-51 51-189-189-189 189Z" />
                    </svg>
                  </button>
                </span>
              </>
            )}
          </div>
          {filters.jurisdiction.length > 0 && <ReactTooltip id="jurisdiction-filter-tooltip" place="bottom" className="tw-z-10" effect="solid" />}

          {activeFilter === "jurisdiction" && (
            <div className="tw-absolute tw-z-10 tw-right-0 tw-mt-1 tw-w-64 tw-rounded-xl tw-bg-white tw-border tw-border-gray-200 tw-shadow-2xl tw-p-3">
              {countryFilterLoading ? (
                <div className="tw-py-4 tw-flex tw-justify-center">
                  <img src={LoaderImg} alt="loading" className="tw-h-8 tw-w-8" />
                </div>
              ) : (
                <>
                  <input type="text" placeholder={`Search ${jurisdictionFilterLabel}`} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-px-3 tw-py-2 tw-text-sm tw-mb-2 focus:tw-ring-primary-purple-500 focus:tw-border-primary-purple-500" />

                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                    <div onClick={handleJurisdictionSelectAll} className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-px-1 tw-py-1.5 tw-text-sm tw-rounded-md">
                      <input type="checkbox" readOnly checked={pendingJurisdiction.length === countryFilterOptions.length} className="tw-h-4 tw-w-4 tw-rounded tw-border-gray-200 tw-accent-primary-purple-600 after:tw-hidden" />
                      Select All
                    </div>
                    <button onClick={applyJurisdiction} disabled={pendingJurisdiction.length === 0 && filters.jurisdiction.length === 0} className="tw-rounded tw-bg-primary-purple-600 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-white disabled:tw-opacity-70 disabled:tw-cursor-not-allowed">
                      Apply
                    </button>
                  </div>

                  <div className="tw-max-h-60 tw-overflow-y-auto tw-space-y-1">
                    {filteredCountries.map((country) => (
                      <div key={country.id} onClick={() => handleJurisdictionToggle(country.key)} className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-px-1 tw-py-1.5 tw-text-sm hover:tw-bg-gray-50 tw-rounded-md">
                        <input type="checkbox" readOnly checked={pendingJurisdiction.includes(country.key)} className="tw-h-4 tw-w-4 tw-rounded tw-border-gray-200 tw-accent-primary-purple-600 after:tw-hidden" />
                        {(country.country_code || country.countryCode) && <ReactCountryFlag countryCode={country.country_code || country.countryCode} svg />}
                        <span className="tw-text-gray-700 tw-capitalize">{country.key}</span>
                      </div>
                    ))}
                    {filteredCountries.length === 0 && <div className="tw-px-1 tw-py-2 tw-text-sm tw-text-gray-500">No results found.</div>}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* HS Code Filter */}
        {activeTab === companySearchTabs.PRODUCT && !hideHsCodeFilter && (
          <div className="tw-relative tw-inline-block">
            <div onClick={() => toggleFilter("hsCode")} className={`tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-border tw-border-[#e7e7e7] tw-cursor-pointer tw-px-3 tw-py-1.5 tw-rounded-lg tw-shadow-button ${activeFilter === "hsCode" ? "tw-bg-gray-100" : "tw-bg-white"} tw-h-8`}>
              <span className="material-symbols-outlined">filter_alt</span>
              <span>HS Code</span>
              {(filters.hsCode || []).length > 0 && (
                <span data-for="hscode-filter-tooltip" data-tip={filters.hsCode.map((code) => code.split(" - ")[0]).join(" | ")} className="tw-bg-primary-purple-100 tw-text-primary-purple-600 tw-pl-1 tw-pr-0.5 tw-text-xs tw-font-medium tw-rounded-full tw-flex tw-items-center">
                  {(filters.hsCode || []).length}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilters((prev) => ({ ...prev, hsCode: [] }));
                      setPendingHsCodes([]);
                    }}
                    className="tw-text-inherit tw-bg-transparent">
                    <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor">
                      <path d="m291-240-51-51 189-189-189-189 51-51 189 189 189-189 51 51-189 189 189 189-51 51-189-189-189 189Z" />
                    </svg>
                  </button>
                </span>
              )}
            </div>
            {(filters.hsCode || []).length > 0 && <ReactTooltip id="hscode-filter-tooltip" place="bottom" className="tw-z-10" effect="solid" />}
          </div>
        )}

        {/* Listed Company Filter */}
        {activeTab === companySearchTabs.COMPANY && (
          <div className="tw-relative tw-inline-block">
            <div onClick={() => toggleFilter("listedCompany")} className={`tw-flex tw-items-center tw-text-xs tw-font-semibold tw-gap-1 tw-border tw-border-gray-[#e7e7e7] tw-cursor-pointer tw-px-3 tw-py-1.5 tw-rounded-lg tw-shadow-button ${activeFilter === "listedCompany" ? "tw-bg-gray-100" : "tw-bg-white"} tw-h-8`}>
              <span className="material-symbols-outlined">filter_alt</span>
              <span>Listed Company</span>
              {filters.listedCompany && (
                <span className="tw-bg-primary-purple-100 tw-text-primary-purple-600 tw-pl-1 tw-pr-0.5 tw-text-xs tw-font-medium tw-rounded-full tw-flex tw-items-center">
                  {1}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilters((prev) => ({ ...prev, listedCompany: null }));
                    }}
                    className="tw-text-inherit tw-bg-transparent">
                    <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor">
                      <path d="m291-240-51-51 189-189-189-189 51-51 189 189 189-189 51 51-189 189 189 189-51 51-189-189-189 189Z" />
                    </svg>
                  </button>
                </span>
              )}
            </div>

            {activeFilter === "listedCompany" && (
              <div className="tw-absolute tw-z-10 tw-mt-1 tw-right-0 tw-w-60 tw-rounded-xl tw-overflow-hidden tw-bg-white tw-border tw-border-gray-200 tw-shadow-2xl">
                <div className="tw-divide-y">
                  {listedCompanyFilter.map((option) => {
                    return (
                      <div key={option.label} onClick={() => handleListedCompanySelect(option)} className={`tw-flex tw-items-center tw-gap-1 tw-cursor-pointer tw-px-4 tw-py-2 tw-text-sm tw-transition-all ${option.label === filters?.listedCompany?.label ? "tw-bg-primary-purple-50 tw-font-medium" : "tw-text-gray-700 hover:tw-bg-gray-50"}`}>
                        <input type="radio" className="!tw-h-4 !tw-m-0 !tw-w-4 tw-accent-primary-purple-600" defaultChecked={option.label === filters?.listedCompany?.label} /> {option.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Trade Metrics Filter */}
        {activeTab === companySearchTabs.PRODUCT && (
          <div className="tw-relative tw-inline-block">
            <div data-for="trade-vol-filter" data-tip={"The Trade Volume filter is only available for date range equal or less than 2 year"} onClick={() => (isTwoYearsOrLess ? toggleFilter("metrics") : () => null)} className={`tw-flex tw-items-center tw-gap-1 tw-border tw-border-[#e7e7e7] tw-cursor-pointer tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-semibold tw-shadow-button ${isTwoYearsOrLess ? "" : "tw-opacity-50"} ${activeFilter === "metrics" ? "tw-bg-gray-100" : "tw-bg-white"} tw-h-8`}>
              <span className="material-symbols-outlined">filter_alt</span>
              <span>Trade Volume</span>
              {getActiveMetrics().length > 0 && (
                <>
                  <span className="tw-bg-primary-purple-100 tw-text-primary-purple-600 tw-pl-1 tw-pr-0.5 tw-text-xs tw-font-medium tw-rounded-full tw-flex tw-items-center">
                    {getActiveMetrics().length}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilters((prev) => ({ ...prev, metrics: initialMetrics }));
                        setPendingMetrics(initialMetrics);
                      }}
                      className=" tw-text-inherit tw-bg-transparent">
                      <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor">
                        <path d="m291-240-51-51 189-189-189-189 51-51 189 189 189-189 51 51-189 189 189 189-51 51-189-189-189 189Z" />
                      </svg>
                    </button>
                  </span>
                </>
              )}
            </div>
            {!isTwoYearsOrLess && <ReactTooltip id="trade-vol-filter" place="bottom" className="tw-z-10" effect="solid" />}

            {activeFilter === "metrics" && (
              <div className="tw-absolute tw-z-10 tw-mt-1 tw-right-0 tw-w-80 tw-rounded-xl tw-bg-white tw-border tw-border-gray-200 tw-shadow-2xl tw-p-3">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                  <span className="tw-text-sm tw-font-medium tw-text-gray-700">Trade Volume</span>
                  <button onClick={applyMetrics} disabled={isMetricsDisabled} className="tw-rounded tw-bg-primary-purple-600 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-white disabled:tw-opacity-70 disabled:tw-cursor-not-allowed">
                    Apply
                  </button>
                </div>
                <div className="tw-space-y-4 tw-max-h-96 tw-overflow-y-auto tw-divide-y">
                  {metricRangeLabels.map((m) => {
                    const currentRange = pendingMetrics[m.key];
                    return (
                      <div key={m.key} className="tw-space-y-2 tw-px-1 tw-py-2">
                        <div className="tw-flex tw-justify-between tw-items-center tw-text-sm tw-font-medium tw-text-gray-900">{m.label}</div>
                        <div className="tw-flex tw-gap-3 tw-items-end">
                          <div className="tw-flex tw-flex-col tw-w-full">
                            <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">from</label>
                            <input type="text" value={currentRange.from} onChange={(e) => handleMetricsUpdate(m.key, "from", e.target.value)} className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm tw-px-3 tw-py-1.5 tw-text-sm focus:tw-border-primary-purple-500 focus:tw-ring-primary-purple-500" />
                          </div>
                          <span className="tw-text-gray-700 tw-pb-1.5">-</span>
                          <div className="tw-flex tw-flex-col tw-w-full">
                            <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">to</label>
                            <input type="text" value={currentRange.to} onChange={(e) => handleMetricsUpdate(m.key, "to", e.target.value)} className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm tw-px-3 tw-py-1.5 tw-text-sm focus:tw-border-primary-purple-500 focus:tw-ring-primary-purple-500" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Clear All Button */}
        {hasActiveFilters && (
          <div className="tw-relative tw-group">
            <button onClick={clearAll} className="tw-flex tw-items-center tw-rounded-full tw-p-2 tw-border tw-border-gray-200 tw-bg-white tw-text-gray-700 tw-h-8">
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#000000">
                <path d="M569-492 294-768h450q23 0 28 10.5t-9 28.5L569-492ZM768-90 528-330v114q0 10-7 17t-17 7h-48q-10 0-17-7t-7-17v-210L90-768l51-51 678 678-51 51Z" />
              </svg>
            </button>
            <div className="tw-absolute tw-right-full tw-top-1/2 -tw-translate-y-1/2 tw-mr-2 tw-px-2 tw-py-1 tw-text-xs tw-text-white tw-bg-gray-800 tw-rounded tw-whitespace-nowrap tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-pointer-events-none">Reset all filters</div>
          </div>
        )}
      </div>
      <Modal centered size="xl" show={showHsCodeModal && !hideHsCodeFilter} onHide={() => setShowHsCodeModal(false)} animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>Filter by HS Code</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {hsCodeFilterLoading ? (
            <div className="tw-py-10 tw-flex tw-justify-center">
              <img src={LoaderImg} alt="loading" className="tw-h-10 tw-w-10" />
            </div>
          ) : (
            <>
              <input type="text" placeholder="Search HS Code" value={hsCodeSearchTerm} onChange={(e) => setHsCodeSearchTerm(e.target.value)} className="tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-px-3 tw-py-2 tw-text-sm tw-mb-3 focus:tw-ring-primary-purple-500 focus:tw-border-primary-purple-500" />
              <div onClick={handleHsCodeSelectAll} className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-px-1 tw-py-2 tw-text-sm tw-rounded-md tw-mb-1">
                <input type="checkbox" readOnly checked={filteredHsCodes.length > 0 && filteredHsCodes.every((item) => pendingHsCodes.includes(item.key))} className="tw-h-4 tw-w-4 tw-rounded tw-border-gray-200 tw-accent-primary-purple-600 after:tw-hidden" />
                Select All
              </div>

              <div className="tw-max-h-72 tw-overflow-y-auto tw-space-y-1 tw-grid tw-grid-cols-2 tw-gap-2">
                {filteredHsCodes.map((item) => (
                  <label key={item.id || item.key} className="tw-flex tw-items-start tw-gap-2 tw-cursor-pointer tw-px-1 tw-py-1.5 tw-text-sm hover:tw-bg-gray-50 tw-rounded-md">
                    <input type="checkbox" readOnly checked={pendingHsCodes.includes(item.key)} onChange={() => handleHsCodeToggle(item.key)} className="tw-h-4 tw-w-4 tw-rounded tw-border-gray-200 tw-accent-primary-purple-600 after:tw-hidden tw-mt-0.5 tw-flex-shrink-0" />
                    <span className="tw-text-gray-700">HS {item.key}</span>
                  </label>
                ))}
                {filteredHsCodes.length === 0 && <div className="tw-px-1 tw-py-2 tw-text-sm tw-text-gray-500">No results found.</div>}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="tw-rounded-lg tw-bg-white tw-text-gray-700 tw-border tw-border-gray-200 tw-py-2 tw-px-4 tw-text-sm" onClick={() => setShowHsCodeModal(false)}>
            Cancel
          </button>
          <button type="button" className="tw-rounded-lg tw-bg-primary-purple-600 tw-text-white tw-py-2 tw-px-4 tw-text-sm" onClick={applyHsCode} disabled={pendingHsCodes.length === 0 && (filters.hsCode || []).length === 0}>
            Apply
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CompanySearchFilters;
